import { useContext } from 'react'
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json'
import { BuilderShell } from './BuilderShell'

/**
 * Élément reactive-json qui monte UNE page du builder (hôte `page`) à
 * l'endroit où le YAML le demande, par son SLUG.
 *
 * POURQUOI DU REACT ICI (rituel config-first) : aucun rjBuild ne peut héberger
 * une page du builder — ses blocs sont des nœuds `builder_unit` qu'il faut
 * résoudre contre le registre des définitions, puis monter un par un (chaque
 * bloc étant lui-même un ReactiveJsonRoot). Seul le couple
 * BuilderProvider/BuilderCanvas sait le faire. Ce composant n'ajoute AUCUNE
 * logique : il traduit une valeur du store (le slug de la page) en point de
 * montage. C'est le pendant exact de [DashboardMount](./DashboardMount.jsx)
 * pour l'hôte `dashboard`.
 *
 * C'est ce qui permet au WIKI de se passer d'un type de contenu à lui : une
 * page documentaire est une `builder_page` rangée dans un répertoire, et son
 * corps se compose en blocs, dans le shell du wiki (arborescence, fil d'Ariane
 * et panneau contextuel restent autour).
 *
 * `slug` vide → ne rend rien : c'est au YAML d'afficher son état vide, pas à
 * ce composant. `readOnly` gouverne l'édition — le YAML le calcule à partir de
 * la présence de `submission_url` sur la page (Drupal accorde l'écriture), pas
 * d'une règle d'accès réécrite côté front.
 *
 * Enregistré dans [App.jsx](../../App.jsx) sous `customPlugins.element`.
 */
export function BuilderPageMount({ props = {} }) {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  const evaluate = (value) => {
    try {
      return evaluateTemplateValue({ valueToEvaluate: value, globalDataContext, templateContext })
    }
    catch {
      return undefined
    }
  }

  const slug = evaluate(props.slug)
  if (slug === undefined || slug === null || slug === '') return null

  // `editPath` : le chemin du magasin qui porte le mode d'édition de l'écran
  // HÔTE. Le fournir donne à la barre de lecture son bouton « Modifier », qui
  // bascule SUR PLACE au lieu de naviguer — c'est ce qui permet à une page du
  // wiki de se lire et de s'éditer à la même adresse, sous la même colonne de
  // gauche. Absent ⇒ barre sans bouton (une page seulement consultable).
  const editPath = String(props.editPath ?? '').replace(/^~~?\./, '')
  const onEdit = editPath
    ? () => globalDataContext.updateData(true, editPath)
    : null

  // `showBar` : en LECTURE, l'en-tête du builder ferait doublon avec la barre
  // d'actions du wiki, qui porte déjà le titre, l'état éditorial et les actions
  // de page — on la masque. En ÉDITION, c'est elle qui porte l'enregistrement
  // des blocs (`/api/builder/save`, distinct de la sauvegarde des métadonnées
  // par l'API decoupled) : la masquer priverait l'éditeur de tout moyen de
  // sauver son contenu. Le YAML tranche donc au cas par cas.
  return (
    <BuilderShell
      slug={String(slug)}
      readOnly={!!evaluate(props.readOnly)}
      embedded
      hosted
      showBar={!!evaluate(props.showBar)}
      onEdit={onEdit}
    />
  )
}

export default BuilderPageMount
